<template>
  <!--
    Spec 21 section 4: when Ladeway publishes a newer terms of service or
    privacy policy, each tenant ADMIN accepts it the next time they use the
    app. Asked of admins only (they can bind the company); everyone else never
    sees this. A native <dialog> with showModal(), like the ops console's
    ActionDialog: the browser makes the page behind it inert and traps focus.
    Escape is refused while acceptance is required, because it is.
  -->
  <dialog
    v-if="required"
    ref="dialog"
    class="lw-c-terms-gate"
    aria-labelledby="terms-gate-title"
    @cancel.prevent
  >
    <h2 id="terms-gate-title">Updated terms</h2>
    <p>
      Ladeway has published
      <template v-if="terms && privacy">new versions of its terms and privacy policy.</template>
      <template v-else-if="terms">a new version of its terms of service.</template>
      <template v-else>a new version of its privacy policy.</template>
      Please read and accept to keep using Ladeway.
    </p>
    <ul>
      <li v-if="terms">
        <a :href="terms.url" target="_blank" rel="noopener">Terms of Service</a>
        <template v-if="terms.effective_on"
          >, effective {{ longDate(terms.effective_on) }}</template
        >
      </li>
      <li v-if="privacy">
        <a :href="privacy.url" target="_blank" rel="noopener">Privacy Policy</a>
        <template v-if="privacy.effective_on"
          >, effective {{ longDate(privacy.effective_on) }}</template
        >
      </li>
    </ul>
    <p v-if="error" class="lw-c-terms-gate__error" role="alert">{{ error }}</p>
    <!-- Secondary, not primary: the page behind keeps its own one primary. -->
    <LwButton variant="secondary" :disabled="busy" @click="accept">
      {{ busy ? 'Saving' : 'I accept' }}
    </LwButton>
  </dialog>
</template>

<script>
import LwButton from './lw/LwButton.vue';

export default {
  name: 'TermsGate',
  components: { LwButton },
  props: {
    user: { type: Object, default: null },
  },
  data() {
    return { required: false, terms: null, privacy: null, busy: false, error: '' };
  },
  watch: {
    user: {
      immediate: true,
      handler(user) {
        if (user && user.role === 'admin') this.check();
      },
    },
  },
  methods: {
    longDate(iso) {
      return new Date(`${iso}T12:00:00Z`).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        timeZone: 'UTC',
      });
    },
    async check() {
      try {
        const res = await this.$http.get('/terms');
        this.terms = res.data.terms;
        this.privacy = res.data.privacy;
        this.required = !!res.data.required;
        if (this.required) {
          await this.$nextTick();
          if (this.$refs.dialog && typeof this.$refs.dialog.showModal === 'function') {
            this.$refs.dialog.showModal();
          }
        }
      } catch {
        // A failed read never blocks the app; the next visit asks again.
        this.required = false;
      }
    },
    async accept() {
      this.busy = true;
      this.error = '';
      try {
        await this.$http.post('/terms/accept', {
          terms_version: this.terms ? this.terms.version : null,
          privacy_version: this.privacy ? this.privacy.version : null,
        });
        if (this.$refs.dialog && this.$refs.dialog.open) this.$refs.dialog.close();
        this.required = false;
      } catch {
        this.error = 'Could not save that. Reload the page and try again.';
      } finally {
        this.busy = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.lw-c-terms-gate {
  max-width: 32rem;
  border: none;
  border-radius: var(--lw-radius-md);
  padding: var(--lw-space-6);
  box-shadow: var(--lw-elev-3-shadow);

  &::backdrop {
    background: var(--lw-elev-scrim-surface);
  }

  h2 {
    margin: 0 0 var(--lw-space-3);
  }

  ul {
    margin: var(--lw-space-3) 0 var(--lw-space-4);
  }
}

.lw-c-terms-gate__error {
  color: var(--lw-feedback-danger-fg);
}
</style>
